/**
 * /zh/release-notes — 发布说明中文 SEO 页。与 ReleaseNotes.tsx 同构，
 * 原生中文，hreflang 双向。
 */
import { MarketingShell } from './MarketingShell';

export function ReleaseNotesZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/release-notes"
      documentTitle="发布说明是什么？含模板 · Draftlize"
      documentDescription="发布说明（release notes）是什么、它和 changelog、发布计划有何不同、该写什么，以及一份模板——并讲清怎么让它和真正上线的东西保持一致。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">指南 · 发布说明</span>
          <h1 className="serif" style={{ fontSize: 'clamp(38px, 4.8vw, 58px)', lineHeight: 1.04, margin: '12px 0 24px' }}>
            发布说明是什么？<br/>
            <em>以及一份忠于「实际上线了什么」的模板。</em>
          </h1>
          <p className="lede">
            <em>发布说明</em>（release notes）是对一次发布里「改了什么」的、给人读的总结——新功能、修复、破坏性变更——写给用产品的人，而不是造产品的人。这页把发布说明到底是什么、它和 changelog、发布计划有何不同、一份好的发布说明该包含什么讲清楚，并给你一份模板。然后是诚实的那部分：发布说明在上线那一刻写一次，随即就开始和它所描述的 spec 与决策对不上，因为没有任何东西把它连回去。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">那么，<em>发布说明是什么？</em></h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--ink-1)', margin: '0 0 18px' }}>
            发布说明是面向用户、用人话写的「这次发布带来了什么」：新增了什么、改进了什么、修复了什么、以及任何需要用户采取行动的事（破坏性变更、迁移）。它的任务是让人不必读 commit 日志就能判断「这次更新我在不在意、我要不要做点什么」。好的<em>产品发布说明</em>以价值开头（「把 thread 导出为 PDF」），而不是实现（「加了一个 PDF 渲染服务」）。
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--ink-1)', margin: 0 }}>
            它和两个常被混为一谈的东西不同。<strong>changelog</strong> 是简短、常面向开发者的「每一项改动」的流水清单（经常自动生成）。<a href="/zh/release-plan-template" style={{ color: 'var(--amber)' }}>发布计划</a> 是<em>面向未来</em>的文档——要发什么、何时、怎么灰度。发布说明是发布<em>之后</em>、<em>回望</em>的那份公告。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">别搞混</span>
            <h2 className="serif">发布说明 vs changelog <em>vs 发布计划。</em></h2>
          </div>
          <p>围绕一次发布的三种构件，容易混。区别在于受众和时间方向。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th>构件</th>
              <th>给谁</th>
              <th>方向</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>发布说明</td><td className="yes">用户 / 客户</td><td>回望——上线了什么</td></tr>
            <tr><td>changelog</td><td className="partial">开发者，简短</td><td>回望——每一项改动</td></tr>
            <tr><td>发布计划</td><td className="partial">内部团队</td><td>前瞻——将要发什么</td></tr>
          </tbody>
        </table>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">模板</span>
            <h2 className="serif">该写什么。<em>随处可抄。</em></h2>
          </div>
          <p>这就是整份模板——它在应用内 changelog、邮件或文档页里都好使。下一节讲的是：当每一条说明连回它所描述的 spec 和决策时，会发生什么变化。</p>
        </div>

        <div style={{ border: '1px solid var(--ink-0)', background: 'var(--paper-0)', fontFamily: "'Inter', sans-serif" }}>
          {[
            { k: '版本与日期', hint: '头部', body: '发布版本（或名称）和上线日期。其余一切都挂在这个锚点上，也是用户在反馈「上周二那个版本」上的问题时所引用的。' },
            { k: '摘要 / 亮点', hint: '以价值开头', body: '一两句话说清这次发布是关于什么的，以结果来表达。读者哪怕只读到这里，也该知道这次更新和自己有没有关系。' },
            { k: '新功能', hint: '新增', body: '每个新能力都用用户的话讲——它让人能做什么，而不是它怎么实现。每条一两句，必要时链到更深的文档。' },
            { k: '改进', hint: '更好了', body: '对既有行为的增强——更快、更清楚、更可靠。这是「我们听到了」那一节；尽量把它们连到促成它的反馈或请求。' },
            { k: '修复', hint: '修好了', body: '用人话写值得一提的已修 bug。你不必列每一个补丁——只列用户注意到或问过的那些。' },
            { k: '破坏性变更 / 需采取的行动', hint: '要做点什么', body: '任何改变了他们所依赖行为、或需要迁移、重新登录、更新集成的东西。一旦适用，它就是最重要的一节——把它埋了，你就在制造工单。' },
          ].map((f, i) => (
            <div key={f.k} style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 24, padding: '20px 22px', borderTop: i === 0 ? 0 : '1px dashed var(--rule)', alignItems: 'baseline' }}>
              <div>
                <span className="serif" style={{ fontSize: 18, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.3 }}>{f.k}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--amber)', fontWeight: 700 }}>{f.hint}</span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.75, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>
        <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, letterSpacing: '.04em', color: 'var(--ink-3)', textAlign: 'center', marginTop: 18 }}>
          这就是模板。下面：为什么发布说明会和「实际上线的东西」对不上——以及我们怎么让它保持一致。
        </p>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">说明描述一次发布。<em>而发布还在动。</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            发布说明是上线那一刻「这次发布本应是什么」的快照。但说明所描述的 spec、以及它底下的决策，一直在变——而没有任何东西把说明连回去。Draftlize 守住这条线。
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">每条说明连到它公告的 spec</h3>
            <p>在 Draftlize 里，发布说明不是凭记忆敲出的自由文本——它从真正上线的卡片生成：功能、修复、它们背后的决策。说明连到它所描述的 spec，于是「这次发布到底改了什么」是一次图查询，而不是在工单堆里做考古。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">临门砍了范围，说明标 stale</h3>
            <p>某个功能在上线前一天被撤，或某个决策在发布后反转。因为说明连着 spec，它自动标记 stale，而不是自信地公告一个根本没上线的东西——那个电子表格抓不到的、丢人的发布说明 bug。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">你的 agent 从「实际上线」起草说明</h3>
            <p>让 Claude Code 或 Cursor 经 MCP 起草下一份发布说明，它读到的是这次发布里真正变动的卡片——于是草稿从决策和 spec 的真实差异出发，而不是某人在上线日傍晚五点重构出来的一份半记得的清单。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          发布说明写一次、再不更新。麻烦在于它所描述的那次发布，一直变到上线那一刻。
        </blockquote>
        <cite>留着这个格式，让说明从「实际上线了什么」生成。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '发布说明和 changelog 有什么区别？', a: '发布说明面向用户、经过编排——用人话解释一次发布意味着什么、以价值开头；changelog 是简短、常面向开发者、经常自动生成的「每一项改动」流水清单。发布说明是给用户的故事，changelog 是给造product 的人的记录。' },
            { q: '发布说明和发布计划有什么区别？', a: '发布计划面向未来——要发什么、何时、怎么灰度和回滚；发布说明回望——发布之后写「发了什么」。计划是内部的、执行性的，说明是对外的、公告形态的。' },
            { q: '好的发布说明该包含什么？', a: '版本与日期、以价值开头的摘要、用用户话讲的新功能、改进、值得一提的修复，以及——一旦适用就最重要的——破坏性变更或需采取的行动。以「这次更新让人能做什么」开头，而不是「它怎么实现」。' },
            { q: '发布说明应该多久发一次？', a: '匹配你的发布节奏。持续交付的团队常把用户可见的改动攒成周期性的汇总，免得每次部署都打扰用户；定期发布则每次发一份。原则是稳定，让用户学会去哪看。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">从「实际上线」生成说明<em>——$5 免费起步。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新账号送 $5</span>
          <span>只为实际用量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            用上面的模板，或让 Draftlize 从真正变动的卡片为每次发布起草说明——连着它们所描述的 spec，于是临门砍范围会去标记说明，而不是上线一句谎话。
          </p>
          <a className="btn-primary" href="/">$5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
